/**
 * UI-facing types for the KnowledgeBaseGrant sharing UX. `KnowledgeBaseGrantSdkService` translates these to/from the embeddings proto messages.
 */
import { NamespaceInterface } from '@vendasta/embeddings';

export type TargetScope = 'self' | 'descendants' | 'all_partners' | 'all_ags';

export type TargetNamespaceType = 'AccountGroup' | 'Partner' | 'MultiLocationGroup' | 'Global';

/** A pickable share target. `identityKey` is a stable (namespaceType, namespaceId, scope) string used for selection, dedup, and `trackBy`. */
export interface TargetOption {
  identityKey: string;
  namespaceId: string;
  namespaceType: TargetNamespaceType;
  scope: TargetScope;
  displayName: string;
  subtitle?: string;
}

/** A persisted grant, shown in the dialog's "Current shares" section. */
export interface KnowledgeBaseGrant {
  grantId: string;
  sourceKBId: string;
  target: TargetOption;
  grantedByUserId: string;
  created: Date;
}

/** A namespace reference for the cleanup APIs that key off (kb, namespace) tuples. Maps to the proto `Namespace` oneof (`Global` carries no id). */
export interface Namespace {
  id: string;
  type: TargetNamespaceType;
}

/** What would block a revoke. Counts drive the blocked-state affordance labels; the sample arrays surface a few names for context. */
export interface GrantDependentSummary {
  downstreamGrantCount: number;
  appAssociationCount: number;
  /** Optional sample of downstream grant target names, for UI context. */
  sampleDownstreamGrants?: string[];
  /** Optional sample of app names whose associations would orphan, for UI context. */
  sampleAppAssociations?: string[];
}

/** Discriminated revoke result. The BE's `FailedPrecondition` is an expected path: the caller renders blocked state from `dependents` rather than throwing. */
export type RevokeResult =
  | { ok: true }
  | { ok: false; dependents: GrantDependentSummary };

/**
 * Discriminated KB-delete result. The BE returns 412 `GRANT_HAS_DEPENDENTS` when the KB still has
 * grants (it's been shared) — an expected path the caller surfaces as a specific message rather than a
 * generic failure. `'error'` covers any other failure.
 */
export type KnowledgeBaseDeleteResult =
  | { ok: true }
  | { ok: false; reason: 'has-grants' | 'error' };

/** Data passed into the dialog via `MAT_DIALOG_DATA`. */
export interface KnowledgeBaseSharingDialogData {
  kbId: string;
  kbName: string;
  /**
   * Hide the "Current shares" list (add-only mode). Set when opened from the detail page's "Shared with"
   * card, which already shows the shares with inline revoke — so the dialog would otherwise be redundant.
   * Left false when opened from a table row, where there's no card and the list is still needed.
   */
  hideCurrentShares?: boolean;
}

/** managedBy sentinel for the caller's own KBs/sources; granted ones carry the partner display name. */
export const OWNED_SENTINEL = 'Owned';

export type TableKSKind = 'url' | 'pdf' | 'file' | 'custom';

/** A knowledge base row in the bases table. */
export interface TableKnowledgeBase {
  id: string;
  /** The KB's owning namespace — threaded into the detail route so it can fetch the KB directly via GetKnowledgeBase. */
  namespace?: NamespaceInterface;
  /** Stable row identity (namespace + id — see kbIdentityKey); ids alone aren't unique across namespaces. */
  kbKey: string;
  name: string;
  description: string;
  sourceCount: number;
  /**
   * IDs of the sources in this KB. A source can belong to many KBs, so membership is owned here (mirrors
   * the proto's KnowledgeBase.knowledgeSourceKeys) rather than denormalized onto each source row.
   */
  sourceIds: string[];
  /** "Owned" sentinel for the caller's own KBs; partner display name for granted KBs. */
  managedBy: string;
  lastModified: Date;
}

/** Everything the dedicated KB detail page renders for a single knowledge base. */
export interface KnowledgeBaseDetail {
  kb: TableKnowledgeBase;
  /** The sources in this KB, resolved to source rows. */
  sources: TableKnowledgeSource[];
  /** Namespaces / partners the KB is shared with (the "Shared with" section). */
  shares: KnowledgeBaseGrant[];
}

/** A knowledge source row in the sources table. */
export interface TableKnowledgeSource {
  id: string;
  kind: TableKSKind;
  name: string;
  subtitle: string;
  description: string;
  managedBy: string;
  lastModified: Date;
  /** Populated in service mode from raw source state — drives the badges. */
  isSuggested?: boolean;
  isTrainingInProgress?: boolean;
  hasAutoRefresh?: boolean;
  autoRefreshTooltip?: string;
  lastScrapedText?: string;
}

/** Helper — build a deterministic identity key for selection / dedup. */
export function targetIdentityKey(
  namespaceType: TargetNamespaceType,
  namespaceId: string,
  scope: TargetScope,
): string {
  return `${namespaceType}:${namespaceId}#${scope}`;
}
